import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Bookmark, ChevronDown, MapPin, Briefcase, Zap, Clock, ArrowRight, LayoutGrid, List, Phone, Mail, Check, Star } from "lucide-react";
import {
  getPublicProfiles,
  getPublicRequirements,
  getMarketplaceProfile,
  MarketplaceProfile,
  MarketplaceRequirement,
  applyToRequirement,
  saveRequirement,
  unsaveRequirement,
  getMarketplaceFilterOptions,
  TalentProfilesFilterOptions,
  JobHiringFilterOptions,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import apiCall from "../../../Redux/ApiCalls/AuthorizedApi";
import { resolveImage } from "../../../Utils/helper";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import styled from "styled-components";
import { checkProfileCompleteness, isAccountFrozen } from "./MarketplaceShared";
import MarketplaceLoader from "./MarketplaceLoader";

import globalReachImg from "../../../assets/images/marketplace/global_reach.webp";

import {
  TalentPageWrap,
  TalentHero,
  TalentHeroEyebrow,
  TalentHeroHeading,
  TalentHeroSub,
  TalentHeroTopRow,
  ViewToggle,
  ViewToggleBtn,
  FilterBar,
  FilterSelect,
  FilterApplyBtn,
  ProfilesSection,
  ProfilesGrid,
  ProfileCard,
  ProfileCardImageWrap,
  ProfileCardImage,
  ProfileCardImageOverlay,
  ProfileCardBadge,
  ProfileCardInnerOverlay,
  ProfileCardRole,
  ProfileCardName,
  ProfileCardBody,
  ProfileSkillTags,
  ProfileSkillTag,
  ProfileMetaRow,
  ProfileMetaItem,
  ProfileMetaLabel,
  ProfileMetaValue,
  PortfolioPreviewLabel,
  PortfolioPreviewGrid,
  PortfolioThumb,
  ViewDossierBtn,
  DiscoverMoreWrap,
  DiscoverMoreBtn,
  GlobalReachSection,
  GlobalReachInner,
  GlobalReachLeft,
  GlobalReachHeading,
  GlobalReachDesc,
  GlobalReachStats,
  GlobalReachStat,
  GlobalReachStatNum,
  GlobalReachStatLabel,
  GlobalReachMapLink,
} from "./MarketplaceTalentPageStyles";

const SKELETON_COUNT = 6;

const ProfileSkeleton: React.FC = () => (
  <ProfileCard style={{ pointerEvents: "none" }}>
    <ProfileCardImageWrap>
      <div style={{ width: "100%", height: "100%", background: "var(--mp-cardBackground)", opacity: 0.5 }} />
    </ProfileCardImageWrap>
    <ProfileCardBody>
      <div style={{ display: "flex", gap: 6 }}>
        {[60, 80, 50].map((w, i) => (
          <div key={i} style={{ height: 20, width: w, borderRadius: 6, background: "var(--mp-cardBackground)", opacity: 0.5 }} />
        ))}
      </div>
      <div style={{ height: 14, width: "70%", borderRadius: 4, background: "var(--mp-cardBackground)", opacity: 0.4 }} />
      <div style={{ height: 36, width: "100%", borderRadius: 12, background: "var(--mp-cardBackground)", opacity: 0.3 }} />
    </ProfileCardBody>
  </ProfileCard>
);

const MarketplaceTalentPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"profile" | "hiring">("profile");
  const [profiles, setProfiles] = useState<MarketplaceProfile[]>([]);
  const [jobs, setJobs] = useState<MarketplaceRequirement[]>([]);
  const [myProfileId, setMyProfileId] = useState<number | null>(null);
  const [revealedContacts, setRevealedContacts] = useState<Record<string, { email: string; phone: string }>>({});
  const [appliedJobs, setAppliedJobs] = useState<Record<string, boolean>>({});
  const [savedRequirements, setSavedRequirements] = useState<Record<string, boolean>>({});

  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [selectedReq, setSelectedReq] = useState<MarketplaceRequirement | null>(null);
  const [applyMessage, setApplyMessage] = useState("");
  const [applyRate, setApplyRate] = useState("");
  const [submittingApply, setSubmittingApply] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [talentFilters, setTalentFilters] = useState<TalentProfilesFilterOptions | null>(null);
  const [jobFilters, setJobFilters] = useState<JobHiringFilterOptions | null>(null);
  const [filtersLoading, setFiltersLoading] = useState(true);

  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedSkills, setSelectedSkills] = useState("");
  const [selectedExperience, setSelectedExperience] = useState("");

  const [activeLocation, setActiveLocation] = useState("");
  const [activeRole, setActiveRole] = useState("");
  const [activeSkills, setActiveSkills] = useState("");
  const [activeExperience, setActiveExperience] = useState("");

  const [search, setSearch] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("search") || "";
  });

  // Load dynamic filter options for talent profiles & job hiring
  useEffect(() => {
    Promise.all([
      getMarketplaceFilterOptions("talent_profiles"),
      getMarketplaceFilterOptions("job_hiring"),
    ])
      .then(([talentRes, jobRes]) => {
        if (talentRes?.talent_profiles) {
          setTalentFilters(talentRes.talent_profiles);
        }
        if (jobRes?.job_hiring) {
          setJobFilters(jobRes.job_hiring);
        }
      })
      .catch(() => { })
      .finally(() => setFiltersLoading(false));
  }, []);

  // Load current user profile ID
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await getMarketplaceProfile();
        if (res?.data?.id) {
          setMyProfileId(res.data.id);
        } else {
          const cached = localStorage.getItem("marketplace_profile");
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed.id) setMyProfileId(parsed.id);
          }
        }
      } catch (e) {
        const cached = localStorage.getItem("marketplace_profile");
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed.id) setMyProfileId(parsed.id);
          } catch { }
        }
      }
    };
    if (localStorage.getItem("access_token")) {
      loadProfile();
    }
    window.addEventListener("marketplace-auth-change", loadProfile);
    return () => {
      window.removeEventListener("marketplace-auth-change", loadProfile);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setSearch(params.get("search") || "");
  }, [window.location.search]);

  const handleTabSwitch = (newTab: "profile" | "hiring") => {
    setActiveTab(newTab);
    setPage(1);
    setSelectedLocation("");
    setSelectedRole("");
    setSelectedSkills("");
    setSelectedExperience("");
    setActiveLocation("");
    setActiveRole("");
    setActiveSkills("");
    setActiveExperience("");
  };

  const fetchData = useCallback(async (pg: number, append = false) => {
    setLoading(true);
    try {
      if (activeTab === "profile") {
        const res = await getPublicProfiles({
          page: pg,
          page_size: 6,
          search: search || undefined,
          location: activeLocation || undefined,
          professional_type: activeRole || undefined,
        });
        const results = res.results ?? [];
        setProfiles(prev => append ? [...prev, ...results] : results);
        setJobs([]);
        setHasMore(!!res.next);
      } else {
        const res = await getPublicRequirements({
          page: pg,
          page_size: 6,
          search: search || undefined,
          location: activeLocation || undefined,
          requirement_type: "service_requirement"
        });
        const results = res.results ?? [];
        setJobs(prev => append ? [...prev, ...results] : results);
        setProfiles([]);
        setHasMore(!!res.next);
      }
    } catch {
      setProfiles([]);
      setJobs([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [activeTab, search, activeLocation, activeRole]);

  useEffect(() => {
    fetchData(1);
  }, [fetchData, search, activeTab, activeLocation, activeRole]);

  // Sync is_saved from API into local state
  useEffect(() => {
    if (jobs.length > 0 && localStorage.getItem("access_token")) {
      const map: Record<string, boolean> = {};
      jobs.forEach(j => { if (j.is_saved) map[j.id] = true; });
      setSavedRequirements(prev => ({ ...prev, ...map }));
    }
  }, [jobs]);

  const handleToggleSaveRequirement = async (e: React.MouseEvent, reqId: string) => {
    e.stopPropagation();
    if (!localStorage.getItem("access_token")) {
      window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      return;
    }
    const wasSaved = !!savedRequirements[reqId];
    setSavedRequirements(prev => ({ ...prev, [reqId]: !wasSaved }));
    try {
      if (wasSaved) {
        await unsaveRequirement(reqId);
        showSuccessToast("Job removed from saved items.");
      } else {
        await saveRequirement(reqId);
        showSuccessToast("Job saved to your wishlist!");
      }
    } catch {
      setSavedRequirements(prev => ({ ...prev, [reqId]: wasSaved }));
      showErrorToast("Could not update saved items. Please try again.");
    }
  };

  const handleRevealContact = async (reqId: string) => {
    const hasToken = !!localStorage.getItem("access_token");
    if (!hasToken) {
      showErrorToast("Please log in first to view contact details.");
      window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      return;
    }
    if (!checkProfileCompleteness()) {
      showErrorToast("Please complete your profile details first to view contact details.");
      navigate("/marketplace/profile/create");
      return;
    }
    try {
      const res = await apiCall({
        method: "POST",
        url: `/api/v1/marketplace/requirements/${reqId}/reveal-contact/`
      });
      if (res) {
        setRevealedContacts(prev => ({
          ...prev,
          [reqId]: {
            email: res.contact_email || "N/A",
            phone: res.contact_phone || "N/A"
          }
        }));
        showSuccessToast("Contact details revealed!");
      }
    } catch {
      showErrorToast("Failed to reveal contact details. Please try again.");
    }
  };

  const handleApplyClick = (req: MarketplaceRequirement) => {
    const hasToken = !!localStorage.getItem("access_token");
    if (!hasToken) {
      showErrorToast("Please log in first to apply.");
      window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      return;
    }
    if (isAccountFrozen()) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot apply to requirements.");
      return;
    }
    if (!checkProfileCompleteness()) {
      showErrorToast("Please complete your profile details first to apply.");
      navigate("/marketplace/profile/create");
      return;
    }
    setSelectedReq(req);
    setApplyMessage("");
    setApplyRate("");
    setIsApplyModalOpen(true);
  };

  const handleSubmitApplication = async () => {
    if (!selectedReq) return;
    setSubmittingApply(true);
    try {
      await applyToRequirement(selectedReq.id, {
        message: applyMessage || undefined,
        proposed_rate: applyRate || undefined
      });
      setAppliedJobs(prev => ({ ...prev, [selectedReq.id]: true }));
      showSuccessToast("Successfully applied for this job requirement!");
      setIsApplyModalOpen(false);
    } catch (err: any) {
      let errMsg = "Failed to submit application. Make sure your profile is set up.";
      if (err?.response?.data) {
        if (typeof err.response.data === "object") {
          errMsg = err.response.data.detail || err.response.data.message || err.response.data.error || Object.values(err.response.data).flat().join(" ") || errMsg;
        } else if (typeof err.response.data === "string") {
          errMsg = err.response.data;
        }
      }
      showErrorToast(errMsg);
    } finally {
      setSubmittingApply(false);
    }
  };

  const handleDiscoverMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchData(nextPage, true);
  };

  const filteredProfiles = profiles.filter(profile => {
    if (activeLocation) {
      const city = (profile.city || "").toLowerCase();
      const state = (profile.state || "").toLowerCase();
      const country = (profile.country || "").toLowerCase();
      const targetLoc = activeLocation.toLowerCase();
      if (!city.includes(targetLoc) && !state.includes(targetLoc) && !country.includes(targetLoc)) return false;
    }
    if (activeRole) {
      const profession = (profile.profession || "").toLowerCase();
      const type = (profile.profile_type || "").toLowerCase();
      const profType = (profile.professional_type || "").toLowerCase();
      const targetRole = activeRole.toLowerCase();
      if (!profession.includes(targetRole) && !type.includes(targetRole) && !profType.includes(targetRole)) return false;
    }
    if (activeSkills) {
      const skills = (profile.skills || []).map(s => s.toLowerCase());
      const workCats = (profile.work_categories || []).map(s => s.toLowerCase());
      const services = (profile.services_offered || []).map(s => s.toLowerCase());
      const targetSkill = activeSkills.toLowerCase();
      if (!skills.some(s => s.includes(targetSkill)) && !workCats.some(s => s.includes(targetSkill)) && !services.some(s => s.includes(targetSkill))) return false;
    }
    if (activeExperience) {
      const exp = profile.experience_years || 0;
      if (activeExperience === "1-3" && (exp < 1 || exp > 3)) return false;
      if (activeExperience === "3-5" && (exp < 3 || exp > 5)) return false;
      if (activeExperience === "5-10" && (exp < 5 || exp > 10)) return false;
      if (activeExperience === "10+" && exp < 10) return false;
    }
    return true;
  });

  const filteredJobs = jobs.filter(job => {
    if (activeLocation) {
      const loc = (job.location || "").toLowerCase();
      if (!loc.includes(activeLocation.toLowerCase())) return false;
    }
    if (activeRole) {
      const title = (job.title || "").toLowerCase();
      const desc = (job.description || "").toLowerCase();
      const targetRole = activeRole.toLowerCase();
      if (!title.includes(targetRole) && !desc.includes(targetRole)) return false;
    }
    if (activeSkills) {
      const desc = (job.description || "").toLowerCase();
      const title = (job.title || "").toLowerCase();
      const targetSkill = activeSkills.toLowerCase();
      if (!desc.includes(targetSkill) && !title.includes(targetSkill)) return false;
    }
    return true;
  });

  const currentFilters = activeTab === "profile" ? talentFilters : jobFilters;

  return (
    <TalentPageWrap>
      {loading && profiles.length === 0 && jobs.length === 0 && (
        <MarketplaceLoader fullScreen />
      )}
      {/* ── HERO ── */}
      <TalentHero style={{ paddingBottom: "24px" }}>
        <TalentHeroEyebrow>Elite Network</TalentHeroEyebrow>
        <TalentHeroTopRow>
          <div>
            <TalentHeroHeading>
              Find the world's finest{" "}
              <span className="gradient">creative</span>
              <br />
              engine.
            </TalentHeroHeading>
            <TalentHeroSub>
              A curated marketplace of vetted cinematographers, editors, and production designers ready for your next masterpiece.
            </TalentHeroSub>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "flex-end" }}>
            <ViewToggle>
              <ViewToggleBtn $active={viewMode === "grid"} onClick={() => setViewMode("grid")} title="Grid View">
                <LayoutGrid />
              </ViewToggleBtn>
              <ViewToggleBtn $active={viewMode === "list"} onClick={() => setViewMode("list")} title="List View">
                <List />
              </ViewToggleBtn>
            </ViewToggle>
            <div style={{ display: "flex", background: "var(--mp-cardBackground, #ffffff)", border: "1px solid rgba(23, 23, 23, 0.1)", borderRadius: "8px", overflow: "hidden" }}>
              <button
                onClick={() => handleTabSwitch("profile")}
                style={{ padding: "8px 16px", border: "none", fontSize: "12px", fontWeight: 700, cursor: "pointer", background: activeTab === "profile" ? "var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%))" : "transparent", color: activeTab === "profile" ? "#fff" : "var(--mp-featureDescColor)" }}
              >
                Talent Profiles
              </button>
              <button
                onClick={() => handleTabSwitch("hiring")}
                style={{ padding: "8px 16px", border: "none", fontSize: "12px", fontWeight: 700, cursor: "pointer", background: activeTab === "hiring" ? "var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%))" : "transparent", color: activeTab === "hiring" ? "#fff" : "var(--mp-featureDescColor)" }}
              >
                Job hiring
              </button>
            </div>
          </div>
        </TalentHeroTopRow>
      </TalentHero>

      {/* ── FILTER BAR ── */}
      <FilterBar style={{ boxSizing: "border-box" }}>
        <FilterSelect style={{ position: "relative" }}>
          <MapPin size={16} />
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            style={{
              border: "none",
              background: "transparent",
              color: "inherit",
              fontFamily: "inherit",
              fontSize: "inherit",
              fontWeight: "inherit",
              outline: "none",
              cursor: "pointer",
              width: "100%",
              marginRight: "4px",
              paddingRight: "20px",
              appearance: "none",
              WebkitAppearance: "none",
            }}
          >
            <option value="" style={{ background: "#1a1d21", color: "#fff" }}>
              {filtersLoading ? "Loading Locations..." : "Location"}
            </option>
            {currentFilters?.location && currentFilters.location.length > 0 ? (
              currentFilters.location.map((item) => (
                <option key={item.value} value={item.value} style={{ background: "#1a1d21", color: "#fff" }}>
                  {item.label}
                </option>
              ))
            ) : null}
          </select>
          <ChevronDown size={14} style={{ position: "absolute", right: "12px", pointerEvents: "none", opacity: 0.6 }} />
        </FilterSelect>

        <FilterSelect style={{ position: "relative" }}>
          <Briefcase size={16} />
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            style={{
              border: "none",
              background: "transparent",
              color: "inherit",
              fontFamily: "inherit",
              fontSize: "inherit",
              fontWeight: "inherit",
              outline: "none",
              cursor: "pointer",
              width: "100%",
              marginRight: "4px",
              paddingRight: "20px",
              appearance: "none",
              WebkitAppearance: "none",
            }}
          >
            <option value="" style={{ background: "#1a1d21", color: "#fff" }}>
              {filtersLoading ? "Loading Roles..." : "Role"}
            </option>
            {currentFilters?.role && currentFilters.role.length > 0 ? (
              currentFilters.role.map((item) => (
                <option key={item.value} value={item.value} style={{ background: "#1a1d21", color: "#fff" }}>
                  {item.label}
                </option>
              ))
            ) : null}
          </select>
          <ChevronDown size={14} style={{ position: "absolute", right: "12px", pointerEvents: "none", opacity: 0.6 }} />
        </FilterSelect>

        <FilterSelect style={{ position: "relative" }}>
          <Zap size={16} />
          <select
            value={selectedSkills}
            onChange={(e) => setSelectedSkills(e.target.value)}
            style={{
              border: "none",
              background: "transparent",
              color: "inherit",
              fontFamily: "inherit",
              fontSize: "inherit",
              fontWeight: "inherit",
              outline: "none",
              cursor: "pointer",
              width: "100%",
              marginRight: "4px",
              paddingRight: "20px",
              appearance: "none",
              WebkitAppearance: "none",
            }}
          >
            <option value="" style={{ background: "#1a1d21", color: "#fff" }}>
              {filtersLoading ? "Loading Skills..." : "Skills"}
            </option>
            {currentFilters?.skills && currentFilters.skills.length > 0 ? (
              currentFilters.skills.map((item) => (
                <option key={item.value} value={item.value} style={{ background: "#1a1d21", color: "#fff" }}>
                  {item.label}
                </option>
              ))
            ) : null}
          </select>
          <ChevronDown size={14} style={{ position: "absolute", right: "12px", pointerEvents: "none", opacity: 0.6 }} />
        </FilterSelect>

        <FilterSelect style={{ position: "relative" }}>
          <Clock size={16} />
          <select
            value={selectedExperience}
            onChange={(e) => setSelectedExperience(e.target.value)}
            style={{
              border: "none",
              background: "transparent",
              color: "inherit",
              fontFamily: "inherit",
              fontSize: "inherit",
              fontWeight: "inherit",
              outline: "none",
              cursor: "pointer",
              width: "100%",
              marginRight: "4px",
              paddingRight: "20px",
              appearance: "none",
              WebkitAppearance: "none",
            }}
          >
            <option value="" style={{ background: "#1a1d21", color: "#fff" }}>
              {filtersLoading ? "Loading Experience..." : "Experience"}
            </option>
            {currentFilters?.experience && currentFilters.experience.length > 0 ? (
              currentFilters.experience.map((item) => (
                <option key={item.value} value={item.value} style={{ background: "#1a1d21", color: "#fff" }}>
                  {item.label}
                </option>
              ))
            ) : null}
          </select>
          <ChevronDown size={14} style={{ position: "absolute", right: "12px", pointerEvents: "none", opacity: 0.6 }} />
        </FilterSelect>

        <FilterApplyBtn onClick={() => {
          setActiveLocation(selectedLocation);
          setActiveRole(selectedRole);
          setActiveSkills(selectedSkills);
          setActiveExperience(selectedExperience);
        }}>
          Apply Filters
        </FilterApplyBtn>
      </FilterBar>

      {/* ── PROFILE CARDS ── */}
      <ProfilesSection>
        {activeTab === "profile" ? (
          <ProfilesGrid $viewMode={viewMode}>
            {filteredProfiles.map((profile) => {
              const avatar = profile.profile_picture ? resolveImage(profile.profile_picture) : "";
              const role = profile.profession || profile.profile_type || "Creative";
              const isMine = myProfileId != null && profile.id === myProfileId;
              const isStudio = profile.profile_type?.toLowerCase() === "studio" || profile.professional_type?.toLowerCase() === "studio";

              let workCategories: string[] = [];
              if (Array.isArray(profile.work_categories) && profile.work_categories.length > 0) {
                workCategories = profile.work_categories;
              } else if (typeof profile.work_categories === "string" && (profile.work_categories as string).trim()) {
                workCategories = (profile.work_categories as string).split(",").map(s => s.trim());
              } else if (Array.isArray(profile.skills) && profile.skills.length > 0) {
                workCategories = profile.skills;
              } else if (typeof profile.skills === "string" && (profile.skills as string).trim()) {
                workCategories = (profile.skills as string).split(",").map(s => s.trim());
              }
              workCategories = workCategories.filter(c => Boolean(c && String(c).trim()));

              const hasCity = Boolean(profile.city && profile.city.trim());
              const hasExp = profile.experience_years != null && String(profile.experience_years).trim() !== "";
              const hasTeamSize = profile.team_size != null && String(profile.team_size).trim() !== "";

              const portfolio = Array.isArray(profile.portfolio)
                ? profile.portfolio.filter(p => p && (p.url || (p as any).image_url))
                : [];

              return (
                <ProfileCard
                  key={profile.id}
                  $viewMode={viewMode}
                  $elevateCenter={true}
                  onClick={() => navigate(`/marketplace/profile/${profile.id}`)}
                >
                  <ProfileCardImageWrap $viewMode={viewMode}>
                    {avatar ? (
                      <ProfileCardImage src={avatar} alt={profile.display_name} />
                    ) : (
                      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Camera size={40} color="var(--mp-heroSubColor)" />
                      </div>
                    )}
                    <ProfileCardImageOverlay />
                    {profile.is_verified && <ProfileCardBadge>Available Now</ProfileCardBadge>}
                    <ProfileCardInnerOverlay>
                      <ProfileCardRole>{role}</ProfileCardRole>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <ProfileCardName>{profile.display_name}</ProfileCardName>
                        {isMine && (
                          <span style={{ fontSize: "9px", padding: "1px 6px", background: "var(--mp-heroBadgeBg, rgba(255, 213, 115, 0.2))", color: "var(--mp-featureIconColor, #D863BA)", borderRadius: "100px", fontWeight: 700 }}>
                            MY PROFILE
                          </span>
                        )}
                      </div>
                      {(profile.profile_avg_rating !== undefined && profile.profile_review_count !== undefined && profile.profile_review_count > 0) && (
                        <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "4px" }}>
                          <Star size={12} fill="#ffb400" color="#ffb400" />
                          <span style={{ fontSize: "12px", fontWeight: 700, color: "#fff" }}>
                            {Number(profile.profile_avg_rating).toFixed(1)}
                          </span>
                          <span style={{ fontSize: "10px", color: "rgba(255,255,255,0.7)" }}>
                            ({profile.profile_review_count})
                          </span>
                        </div>
                      )}
                    </ProfileCardInnerOverlay>
                  </ProfileCardImageWrap>

                  <ProfileCardBody>
                    {/* Work Categories Badges Below Image */}
                    {workCategories.length > 0 && (
                      <ProfileSkillTags>
                        {workCategories.slice(0, 4).map((cat, idx) => (
                          <ProfileSkillTag key={idx} style={{ borderRadius: "20px", padding: "4px 12px" }}>
                            {cat}
                          </ProfileSkillTag>
                        ))}
                      </ProfileSkillTags>
                    )}

                    {/* Horizontal Line 1 */}
                    <div style={{ width: "100%", height: "1px", background: "var(--mp-borderColor, rgba(255, 255, 255, 0.1))", opacity: 0.5 }} />

                    {/* City & Exp (Freelancer) OR City & Team Size (Studio) */}
                    {(hasCity || (isStudio ? hasTeamSize : hasExp)) && (
                      <ProfileMetaRow style={{ justifyContent: "space-between" }}>
                        {hasCity && (
                          <ProfileMetaItem style={{ flex: 1 }}>
                            <ProfileMetaLabel>CITY</ProfileMetaLabel>
                            <ProfileMetaValue>{profile.city}</ProfileMetaValue>
                          </ProfileMetaItem>
                        )}
                        {isStudio ? (
                          hasTeamSize && (
                            <ProfileMetaItem style={{ flex: 1, textAlign: hasCity ? "right" : "left" }}>
                              <ProfileMetaLabel>TEAM SIZE</ProfileMetaLabel>
                              <ProfileMetaValue>{profile.team_size} Members</ProfileMetaValue>
                            </ProfileMetaItem>
                          )
                        ) : (
                          hasExp && (
                            <ProfileMetaItem style={{ flex: 1, textAlign: hasCity ? "right" : "left" }}>
                              <ProfileMetaLabel>EXP</ProfileMetaLabel>
                              <ProfileMetaValue>{profile.experience_years} Yrs</ProfileMetaValue>
                            </ProfileMetaItem>
                          )
                        )}
                      </ProfileMetaRow>
                    )}

                    {/* Horizontal Line 2 & Portfolio Preview (only if non-empty) */}
                    {portfolio.length > 0 && (
                      <>
                        <div style={{ width: "100%", height: "1px", background: "var(--mp-borderColor, rgba(255, 255, 255, 0.1))", opacity: 0.5 }} />
                        <PortfolioPreviewLabel>PORTFOLIO PREVIEW</PortfolioPreviewLabel>
                        <PortfolioPreviewGrid>
                          {portfolio.slice(0, 3).map((item, idx) => (
                            <PortfolioThumb key={idx}>
                              <img src={resolveImage(item.url || (item as any).image_url)} alt={item.title || "Portfolio"} />
                            </PortfolioThumb>
                          ))}
                        </PortfolioPreviewGrid>
                      </>
                    )}

                    {/* CTA */}
                    <ViewDossierBtn
                      $viewMode={viewMode}
                      onClick={(e) => { e.stopPropagation(); navigate(`/marketplace/profile/${profile.id}`); }}
                    >
                      View Full Dossier <ArrowRight size={14} />
                    </ViewDossierBtn>
                  </ProfileCardBody>
                </ProfileCard>
              );
            })}
          </ProfilesGrid>
        ) : (
          <ProfilesGrid $viewMode={viewMode} $isHiringTab={true}>
            {filteredJobs.map((job) => {
              const revealed = revealedContacts[job.id] || (job.is_contact_revealed ? { email: job.contact_email || "N/A", phone: job.contact_phone || "N/A" } : null);
              const applied = appliedJobs[job.id];
              const cachedUser = localStorage.getItem("user_info") ? JSON.parse(localStorage.getItem("user_info")!) : null;
              const cachedProfile = localStorage.getItem("marketplace_profile") ? JSON.parse(localStorage.getItem("marketplace_profile")!) : null;
              const myName = cachedProfile?.display_name || cachedUser?.name || cachedUser?.username || "";
              const isMine = (myProfileId != null && job.profile_id === myProfileId) ||
                (myName && job.poster_name && myName.toLowerCase() === job.poster_name.toLowerCase());
              return (
                <ProfileCard key={job.id} $viewMode={viewMode} $elevateCenter={false} style={{ cursor: "default" }}>
                  <ProfileCardBody style={{ gap: "14px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "var(--mp-headingColor)" }}>{job.title}</h3>
                        {isMine && (
                          <span style={{ alignSelf: "flex-start", fontSize: "9px", padding: "1px 6px", background: "var(--mp-heroBadgeBg, rgba(255, 213, 115, 0.2))", color: "var(--mp-featureIconColor, #D863BA)", borderRadius: "100px", fontWeight: 700 }}>
                            MY POST
                          </span>
                        )}
                      </div>
                      {/* Save / Unsave heart button */}
                      <button
                        onClick={(e) => handleToggleSaveRequirement(e, job.id)}
                        title={savedRequirements[job.id] ? "Remove from saved" : "Save job"}
                        style={{
                          background: savedRequirements[job.id] ? "linear-gradient(135deg, #ff6b6b 0%, #c94daa 100%)" : "rgba(0,0,0,0.05)",
                          border: "none",
                          borderRadius: "50%",
                          width: 32,
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          color: savedRequirements[job.id] ? "#ffffff" : "var(--mp-featureDescColor, #71717a)",
                          flexShrink: 0,
                          transition: "all 0.2s ease",
                          boxShadow: savedRequirements[job.id] ? "0 2px 10px rgba(201,77,170,0.4)" : "none",
                        }}
                      >
                        <Bookmark size={15} fill={savedRequirements[job.id] ? "#ffffff" : "none"} />
                      </button>
                    </div>

                    <p style={{ margin: 0, fontSize: "13px", lineHeight: "1.5", color: "var(--mp-featureDescColor)" }}>
                      {job.description || "Production crew hiring requirement."}
                    </p>

                    <div style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "12px", background: "var(--mp-bodyBackground, rgba(0,0,0,0.03))", padding: "10px", borderRadius: "8px" }}>
                      <div style={{ color: "var(--mp-textColor)" }}><strong>Location:</strong> {job.location || "Anywhere"}</div>
                      <div style={{ color: "var(--mp-textColor)" }}><strong>Budget:</strong> {job.budget ? `₹${job.budget}` : "Negotiable"}</div>

                      {revealed ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px", borderTop: "1px solid rgba(0,0,0,0.08)", paddingTop: "6px", marginTop: "4px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "green", fontWeight: 600 }}>
                            <Phone size={12} /> {revealed.phone}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "green", fontWeight: 600 }}>
                            <Mail size={12} /> {revealed.email}
                          </div>
                        </div>
                      ) : (
                        !isMine ? (
                          <button
                            onClick={() => handleRevealContact(job.id)}
                            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", background: "none", border: "1px dashed #c94daa", color: "#c94daa", fontSize: "11px", fontWeight: 700, padding: "6px", borderRadius: "6px", cursor: "pointer", marginTop: "4px" }}
                          >
                            <Phone size={10} /> View Contact Details
                          </button>
                        ) : null
                      )}
                    </div>

                    <div style={{ marginTop: "auto", display: "flex", gap: "8px" }}>
                      {applied ? (
                        <button disabled style={{ flex: 1, padding: "12px", borderRadius: "12px", border: "none", background: "#e2e8f0", color: "#64748b", fontWeight: 700, fontSize: "13px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                          <Check size={14} /> Applied
                        </button>
                      ) : (
                        <button
                          disabled={isMine}
                          onClick={() => {
                            if (!isMine) handleApplyClick(job);
                          }}
                          style={{
                            flex: 1,
                            padding: "12px",
                            borderRadius: "12px",
                            border: "none",
                            background: isMine ? "#e2e8f0" : "var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%))",
                            color: isMine ? "#64748b" : "#fff",
                            fontWeight: 700,
                            fontSize: "13px",
                            cursor: isMine ? "not-allowed" : "pointer"
                          }}
                        >
                          Apply for Job
                        </button>
                      )}
                    </div>
                  </ProfileCardBody>
                </ProfileCard>
              );
            })}
          </ProfilesGrid>
        )}

        {/* Discover More */}
        {hasMore && (
          <DiscoverMoreWrap>
            <DiscoverMoreBtn onClick={handleDiscoverMore} disabled={loading}>
              {loading ? "Loading…" : "Discover More"}
              <ChevronDown />
            </DiscoverMoreBtn>
          </DiscoverMoreWrap>
        )}
      </ProfilesSection>

      {/* ── GLOBAL REACH ── */}
      <GlobalReachSection>
        <GlobalReachInner>
          <GlobalReachLeft>
            <GlobalReachHeading>
              Global Reach.<br />
              <span className="accent">Local Expertise.</span>
            </GlobalReachHeading>
            <GlobalReachDesc>
              Our network spans across 40+ countries. Find local crews to minimise travel costs and navigate regional production requirements with ease.
            </GlobalReachDesc>
            <GlobalReachStats>
              <GlobalReachStat>
                <GlobalReachStatNum>2.4k+</GlobalReachStatNum>
                <GlobalReachStatLabel>Active HRs</GlobalReachStatLabel>
              </GlobalReachStat>
              <GlobalReachStat>
                <GlobalReachStatNum>1.8k+</GlobalReachStatNum>
                <GlobalReachStatLabel>Active in London</GlobalReachStatLabel>
              </GlobalReachStat>
            </GlobalReachStats>
            <GlobalReachMapLink>
              Explore our Global Production Map <ArrowRight size={14} />
            </GlobalReachMapLink>
          </GlobalReachLeft>

          <img src={globalReachImg} alt="Global Reach Map" style={{ width: '100%', display: 'block' }} />
        </GlobalReachInner>
      </GlobalReachSection>

      {isApplyModalOpen && selectedReq && (
        <ModalOverlay onClick={() => setIsApplyModalOpen(false)}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Submit Application</ModalTitle>
              <CloseBtn onClick={() => setIsApplyModalOpen(false)}>&times;</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <ModalField>
                <label style={{ fontWeight: 600, color: "var(--mp-textColor, #171717)" }}>Applying for:</label>
                <span style={{ fontSize: "14px", color: "var(--mp-headingColor, #171717)", fontWeight: 700 }}>{selectedReq.title}</span>
              </ModalField>
              <ModalField>
                <label htmlFor="coverNote">Cover Note / Pitch (optional)</label>
                <textarea
                  id="coverNote"
                  value={applyMessage}
                  onChange={(e) => setApplyMessage(e.target.value)}
                  placeholder="Introduce yourself, mention your experience or details of the equipment/service you can offer..."
                  rows={4}
                />
              </ModalField>
              <ModalField>
                <label htmlFor="proposedRate">Proposed Rate (₹, optional)</label>
                <input
                  id="proposedRate"
                  type="number"
                  value={applyRate}
                  onChange={(e) => setApplyRate(e.target.value)}
                  placeholder="e.g. 15000"
                />
              </ModalField>
            </ModalBody>
            <ModalFooter>
              <CancelBtn onClick={() => setIsApplyModalOpen(false)}>Cancel</CancelBtn>
              <SubmitBtn disabled={submittingApply} onClick={handleSubmitApplication}>
                {submittingApply ? "Submitting..." : "Submit Application"}
              </SubmitBtn>
            </ModalFooter>
          </ModalContainer>
        </ModalOverlay>
      )}
    </TalentPageWrap>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: var(--mp-cardBackground, #ffffff);
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 24px;
  gap: 20px;
  animation: modalFadeIn 0.3s ease-out;

  @keyframes modalFadeIn {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 12px;
  
  [data-marketplace-theme="dark"] & {
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);
  font-family: Montserrat, sans-serif;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--mp-textColor, #71717a);
  &:hover {
    color: var(--mp-headingColor, #171717);
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModalField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 13px;
    font-weight: 600;
    color: var(--mp-textColor, #4b5563);
  }

  textarea, input {
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    background: var(--mp-inputBackground, #ffffff);
    color: var(--mp-textColor, #171717);
    font-size: 14px;
    outline: none;
    font-family: inherit;

    [data-marketplace-theme="dark"] & {
      border-color: rgba(255, 255, 255, 0.15);
      background: rgba(255, 255, 255, 0.03);
    }

    &:focus {
      border-color: #c94daa;
    }
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding-top: 16px;

  [data-marketplace-theme="dark"] & {
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const CancelBtn = styled.button`
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: none;
  color: var(--mp-textColor, #4b5563);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  
  [data-marketplace-theme="dark"] & {
    border-color: rgba(255, 255, 255, 0.15);
  }

  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

const SubmitBtn = styled.button`
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  background: var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%));
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default MarketplaceTalentPage;
